import dayjs from 'dayjs'
import type { DeviceDetails, ModalField } from '../interfaces'
import { findDeviceLabelByUrl } from './reportTypeOptions'

// Detectores simples para tipos de ações/rotas/display
export function isConnectDevice(details: DeviceDetails): boolean {
  return details.route === '/auth/operator/login/device'
}

export function isDisconnectDevice(details: DeviceDetails): boolean {
  return details.route === '/auth/logout/device/external'
}

export function isReplaceDevice(details: DeviceDetails): boolean {
  return details.route === '/device/logs/transfer-crew'
}

export function isCrewComposition(details: DeviceDetails): boolean {
  return (
    details.route === '/device/logs/crew-composition' ||
    details.route === '/device/logs/change-composition'
  )
}

export function isDeviceTransfer(details: DeviceDetails): boolean {
  return details.route === '/device/transfer'
}

export function isEditImei(details: DeviceDetails): boolean {
  return (
    (!!details.route &&
      String(details.route).includes('change-identifications')) ||
    details.action === 'edit_identification' ||
    (!!details.action_display &&
      String(details.action_display).toLowerCase().includes('imei')) ||
    (!!details.action_display &&
      String(details.action_display).toLowerCase().includes('tracking'))
  )
}

export function isEditIdentification(details: DeviceDetails): boolean {
  // Detecta PUT /device/{id} (editar dispositivo completo)
  return details.request_type === 'PUT' && !!details.route && /^\/device\/\d+$/.test(String(details.route))
}

export function isBackToAvailable(details: DeviceDetails): boolean {
  return details.route === '/device/back-to-available'
}

export function isBackToService(details: DeviceDetails): boolean {
  return details.route === '/device/back-to-service'
}

export function isOutOfService(details: DeviceDetails): boolean {
  return details.route === '/device/out-of-service'
}

export function isLastDispatches(details: DeviceDetails): boolean {
  return details.route === '/device/last-dispatches'
}

export function isSetOnMission(details: DeviceDetails): boolean {
  return details.route === '/device/set-on-mission'
}

export function isActivateDevice(details: DeviceDetails): boolean {
  return (
    (!!details.route &&
      String(details.route).includes('activate-deactivate')) ||
    details.action === 'activate_device' ||
    (!!details.action_display &&
      String(details.action_display).toLowerCase().includes('activate')) ||
    (!!details.action_display &&
      String(details.action_display).toLowerCase().includes('deactivate'))
  )
}

export function isCreateDevice(details: DeviceDetails): boolean {
  return details.route === '/device/store'
}

// Formata um objeto de usuário (representation_name + registration)
export function formatUserLabel(user: unknown): string | undefined {
  if (!user) return undefined
  if (typeof user === 'string') return user
  if (typeof user === 'object') {
    const obj = user as Record<string, unknown>
    const rep = (obj['representation_name'] ?? obj['name']) as string | undefined
    const registration = obj['registration'] as string | undefined

    if (typeof rep === 'string' && rep.trim()) {
      if (typeof registration === 'string' && registration.trim()) return `[${registration}] ${rep}`
      return rep
    }

    const login = obj['login']
    if (typeof login === 'string' || typeof login === 'number') return String(login)
    const id = obj['id']
    if (typeof id === 'string' || typeof id === 'number') return String(id)
  }

  return undefined
}

// Extrai até 5 códigos únicos de diversos locais
export function extractLastDispatchCodes(
  details: DeviceDetails
): readonly string[] {
  // prefer top-level occurrences_viewed (quando presente no payload)
  if (
    details.occurrences_viewed &&
    Array.isArray(details.occurrences_viewed) &&
    details.occurrences_viewed.length
  ) {
    const codes = details.occurrences_viewed.reduce<readonly string[]>(
      (acc, item) => {
        if (!item) return acc
        const code = item.code
        if (typeof code === 'string' && code.trim())
          return acc.includes(code.trim()) ? acc : [...acc, code.trim()]
        return acc
      },
      [] as readonly string[]
    )

    return codes.length ? codes.slice(0, 5) : []
  }

  // tenta varias formas dentro de content
  const content = details.content
  const parse = (value: unknown): unknown => {
    if (typeof value === 'string') {
      try {
        return JSON.parse(value)
      } catch {
        return value
      }
    }
    return value
  }

  const findArray = (value: unknown): readonly unknown[] | undefined => {
    const parsed = parse(value)
    if (Array.isArray(parsed)) return parsed
    if (parsed && typeof parsed === 'object') {
      const obj = parsed as Record<string, unknown>
      for (const key of [
        'occurrences_viewed',
        'dispatches',
        'data',
        'items',
        'result',
        'lastDispatches',
        'rows',
      ]) {
        const candidate = obj[key]
        if (Array.isArray(candidate)) return candidate
      }
    }
    return undefined
  }

  const findCodesInArray = (arr: readonly unknown[]) =>
    arr.reduce<readonly string[]>((acc, item) => {
      if (!item || typeof item !== 'object') return acc
      const it = item as Record<string, unknown>
      const maybeCode =
        it['code'] ??
        it['dispatch_code'] ??
        it['dispatchCode'] ??
        it['id'] ??
        it['reference']
      if (typeof maybeCode === 'string' && maybeCode.trim()) {
        const trimmed = maybeCode.trim()
        return acc.includes(trimmed) ? acc : [...acc, trimmed]
      }
      if (typeof maybeCode === 'number') {
        const num = String(maybeCode)
        return acc.includes(num) ? acc : [...acc, num]
      }
      return acc
    }, [] as readonly string[])

  if (content) {
    const arr = findArray(content)
    if (arr && arr.length) return findCodesInArray(arr).slice(0, 5)

    // fallback: if content is simple string, extract tokens
    if (typeof content === 'string') {
      const found = content.match(/[A-Z0-9\-]{3,}/gi)
      if (found && found.length) return Array.from(new Set(found)).slice(0, 5)
    }
  }

  const fallbackText =
    details.comment ??
    (typeof details.content === 'string' ? details.content : undefined)
  if (fallbackText) {
    const found = String(fallbackText).match(/[A-Z0-9\-]{3,}/gi)
    if (found && found.length) return Array.from(new Set(found)).slice(0, 5)
  }

  return []
}

// Extract names for replace/transfer action
export function extractReplaceDeviceNames(details: DeviceDetails): {
  readonly origin?: string | null
  readonly destination?: string | null
} {
  let origin = details.from_device?.name ?? details.from_device_name ?? null
  let destination = details.to_device?.name ?? details.to_device_name ?? null

  if ((!origin || !destination) && details.content) {
    try {
      const parsed =
        typeof details.content === 'string'
          ? JSON.parse(details.content)
          : details.content
      const parsedObj = parsed as Record<string, unknown>

      if (!origin) {
        const tryKeys = [
          'fromDeviceName',
          'from_device_name',
          'from_device',
          'from',
        ]
        for (const key of tryKeys) {
          const candidateValue = parsedObj[key]
          if (typeof candidateValue === 'string') {
            origin = candidateValue
            break
          }
          if (candidateValue && typeof candidateValue === 'object') {
            const nestedName = (candidateValue as Record<string, unknown>)[
              'name'
            ]
            if (typeof nestedName === 'string') {
              origin = nestedName
              break
            }
          }
        }
      }

      if (!destination) {
        const tryKeys = ['toDeviceName', 'to_device_name', 'to_device', 'to']
        for (const key of tryKeys) {
          const candidateValue = parsedObj[key]
          if (typeof candidateValue === 'string') {
            destination = candidateValue
            break
          }
          if (candidateValue && typeof candidateValue === 'object') {
            const nestedName = (candidateValue as Record<string, unknown>)[
              'name'
            ]
            if (typeof nestedName === 'string') {
              destination = nestedName
              break
            }
          }
        }
      }
    } catch {
      // ignore
    }
  }

  destination = destination ?? details.device?.name ?? null

  return { origin, destination }
}

export function buildBaseUserFields(
  details: DeviceDetails,
  translate: (key: string) => string
): readonly ModalField[] {
  // Determine origin display: prefer action_station.name when origin is station, or device name when origin is device
  let originLabel = translate('Origin') || 'Origem'
  let originValue: string | number | null | undefined = details.origin ?? null

  if (details.origin === 'station' || details.action_station?.name) {
    originLabel = translate('Station') || 'Estação'
    originValue = details.action_station?.name ?? originValue
  } else if (details.origin === 'device' || details.device) {
    originLabel = translate('Device:name') || 'Dispositivo'
    // device can be object or string
    originValue = details.device?.name ?? originValue
  }

  const actionLabelFromRoute = details.route
    ? findDeviceLabelByUrl(details.route, translate)
    : undefined

  const baseFields: readonly ModalField[] = [
    { label: translate('User') || 'Usuário', value: formatUserLabel(details.action_user) ?? details['user:representation_name'] ?? 'Não informado' },
    {
      label: translate('Action') || 'Ação',
      value:
        actionLabelFromRoute ??
        details.action_display ??
        details['action'] ??
        'Não informado',
    },
    { label: originLabel, value: originValue ?? 'Não informado' },
  ]

  const stationField: readonly ModalField[] = [
    {
      label: 'Estação',
      value: details.action_station?.name ?? 'Não informado',
    },
  ]

  const performedAtField: readonly ModalField[] = [
    {
      label: translate('Performed at') || 'Realizado em',
      value: details.created_at
        ? dayjs(details.created_at).format('DD/MM/YYYY HH:mm')
        : 'Não informado',
    },
  ]

  return originLabel === (translate('Station') || 'Estação')
    ? [...baseFields, ...performedAtField]
    : [...baseFields, ...stationField, ...performedAtField]
}

export function buildDeviceNameField(details: DeviceDetails): ModalField {
  return {
    label: 'Nome do Dispositivo',
    value: details.device?.name ?? 'Não informado',
  }
}
